import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { KMark } from "@/components/KMark";
import { Compass, MapTrifold, Calendar, BookmarkSimple, User, Scissors, ChartLine, Users, Storefront, Moon } from "@phosphor-icons/react";

const memberTabs = [
  { to: "/discover", label: "Discover", icon: Compass, id: "tab-discover" },
  { to: "/explore", label: "Explore", icon: MapTrifold, id: "tab-explore" },
  { to: "/upcoming", label: "Upcoming", icon: Calendar, id: "tab-upcoming" },
  { to: "/saved", label: "Saved", icon: BookmarkSimple, id: "tab-saved" },
  { to: "/me", label: "My KUTZ", icon: User, id: "tab-me" },
];

const proTabs = [
  { to: "/my-studio", label: "My Studio", icon: Storefront, id: "tab-studio" },
  { to: "/calendar", label: "Calendar", icon: Calendar, id: "tab-calendar" },
  { to: "/bookings", label: "Bookings", icon: Users, id: "tab-bookings" },
  { to: "/pro/requests", label: "Requests", icon: Moon, id: "tab-requests" },
  { to: "/earnings", label: "Earnings", icon: ChartLine, id: "tab-earnings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isPro = user && (user.role === "professional" || user.role === "owner");
  const tabs = isPro ? proTabs : memberTabs;

  // HQ has its own dedicated chrome — skip member/pro Layout entirely
  if (loc.pathname.startsWith("/hq")) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-ink text-cream">
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-home">
            <KMark size={28} />
            <span className="font-display text-xl tracking-tight">KUTZ</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {tabs.map((t) => (
              <NavLink
                key={t.to} to={t.to} data-testid={t.id}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.2em] transition-colors ${isActive ? "text-gold" : "text-cream/70 hover:text-cream"}`
                }
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <button onClick={logout} data-testid="logout-btn" className="btn-ghost px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em]">Sign out</button>
            ) : (
              <Link to="/auth" data-testid="sign-in-btn" className="btn-gold px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-semibold">Sign in</Link>
            )}
          </div>
        </div>
      </header>

      <main className="pb-28 md:pb-16">{children}</main>

      {/* Mobile bottom tab bar */}
      <nav className="glass-bottom md:hidden fixed bottom-0 inset-x-0 z-50">
        <div className="grid grid-cols-5 h-16">
          {tabs.map((t) => {
            const active = loc.pathname === t.to || loc.pathname.startsWith(t.to + "/");
            const Icon = t.icon;
            return (
              <NavLink key={t.to} to={t.to} data-testid={`m-${t.id}`} className="flex flex-col items-center justify-center gap-1">
                <Icon size={22} weight={active ? "fill" : "regular"} color={active ? "#C5A059" : "#FAF9F6"} />
                <span className={`text-[10px] uppercase tracking-[0.2em] ${active ? "text-gold" : "text-cream/70"}`}>{t.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <footer className="hidden md:block border-t border-cream/10 mt-16">
        <div className="max-w-6xl mx-auto px-5 py-10 flex items-center justify-between">
          <p className="text-xs text-cream/50 uppercase tracking-[0.3em]">Good hair isn't luck, it's KUTZ.</p>
          <p className="text-xs text-cream/40">© KUTZ. Every decision creates more opportunity than it removes.</p>
        </div>
      </footer>
    </div>
  );
}
